import { effect, inject, Injectable, signal } from '@angular/core';
import { GeoJSON, Position } from 'geojson';
import { GeoJSONSource, LngLat, MapGeoJSONFeature, MapMouseEvent, PointLike } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';
// @ts-expect-error No type declaration type exists
import { stringify as wktStringify } from 'wkt';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { layerSymbolStyle } from '@connect-services/client/models/highlight-entity.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { Entity } from '@connect-services/client/services/create-field-intervention.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { GeohashService } from '@connect-services/client/services/geohash.service';
import { GeometryEditPreviewService } from '@connect-services/client/services/geometry-edit-preview.service';
import { MapGeohashService } from '@connect-services/client/services/map/geohash/map-geohash.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapEditModeService, TARGET_CURSOR } from '@connect-services/client/services/map-edit-mode.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapSourceService, POINT_INDEX } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    EditAction,
    Feature,
    FeatureCollection,
    Geometry,
    GeometryService,
    Layer,
    LayersService,
    LineString,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point as SdkPoint,
    Polygon,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

type GeomEditContext = {
    addVertex: boolean;
    entityId: string;
    geometry?: Geometry;
    layerId: string;
    multiGeomIndex: number;
    selectedPointIndex: number;
};

const editModeComplexType: [
    typeof Geometry.TypeEnum.POLYGON,
    typeof Geometry.TypeEnum.MULTI_POLYGON,
    typeof Geometry.TypeEnum.LINE_STRING,
    typeof Geometry.TypeEnum.MULTI_LINE_STRING,
    typeof Geometry.TypeEnum.POINT,
    typeof Geometry.TypeEnum.MULTI_POINT,
] = [
    LineString.TypeEnum.LINE_STRING,
    MultiLineString.TypeEnum.MULTI_LINE_STRING,
    MultiPoint.TypeEnum.MULTI_POINT,
    MultiPolygon.TypeEnum.MULTI_POLYGON,
    Polygon.TypeEnum.POLYGON,
    SdkPoint.TypeEnum.POINT,
];

const polygonType: [typeof Geometry.TypeEnum.MULTI_POLYGON, typeof Geometry.TypeEnum.POLYGON] = [
    MultiPolygon.TypeEnum.MULTI_POLYGON,
    Polygon.TypeEnum.POLYGON,
];

const pointType: [typeof Geometry.TypeEnum.POINT, typeof Geometry.TypeEnum.MULTI_POINT] = [
    SdkPoint.TypeEnum.POINT,
    MultiPoint.TypeEnum.MULTI_POINT,
];

const DEFAULT_SNAP_LAYOUT_STYLE = {
    'icon-color': 'rgb(0, 0, 0)',
    'icon-halo-blur': 0,
    'icon-halo-color': 'rgb(0, 0, 0)',
    'icon-halo-width': 0,
};

type SimpleGeometryWithError = {
    error: string | null;
    geometry: Polygon | LineString | MultiPoint | null;
};

type ComplexGeometryWithError = {
    error: string | null;
    geometry: MultiPolygon | MultiLineString | null;
};

@Injectable({
    providedIn: 'root',
})
export class MapEditGeomService extends MapBaseService {
    public static readonly MAP_EDIT_PHANTOM_LAYER_ID = 'map-edit-phantom-layer';

    private static readonly EARTH_RADIUS = 6371000;
    private static readonly MAP_EDIT_PHANTOM_SOURCE_ID = 'map-edit-phantom-source';

    public readonly editedLayerId = signal<Set<string>>(new Set<string>());
    public nextPoints: Position[] = [];
    public previousPoints: Position[] = [];
    public readonly selectedSnapPoint = signal<{ position: Position; uniqueId: string } | null>(null);
    public readonly snapTolerance = signal(0);

    private broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly businessIdentifier = signal<{ key: string; value: unknown }>({ key: '', value: '' });
    private readonly currentEntityId = signal<string>('');
    private drawEditGeomPhantomLineFunctionInstance?: (mapMouseEvent: MapMouseEvent) => void;
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly geohashService = inject(GeohashService);
    private readonly geomEditContexts = signal<Record<string, GeomEditContext[]>>({});
    private readonly geometryEditPreviewService = inject(GeometryEditPreviewService);
    private readonly geometryService = inject(GeometryService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapGeohashService = inject(MapGeohashService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sdkLayersService = inject(LayersService);
    private readonly snackBarService = inject(SnackBarService);
    private snapVertexHighlightFunctionInstance?: (mapMouseEvent: MapMouseEvent) => void;
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        super();

        this.broadcastChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<MapBaseSource<unknown>>>,
        ): Promise<void> => {
            if (
                event.data.source !== BroadcastChannelSourceEnum.ENTITY_DETAIL ||
                event.data.message !== BroadcastChannelMessageEnum.REDRAW_SOURCE ||
                !event.data.additionalData
            ) {
                return;
            }

            await this.drawSource(event.data.additionalData);
        };

        effect(() => {
            if (this.mapEditModeService.isMapEditModeActive()) {
                return;
            }

            this.mapEditModeService.selectedSnapSources.set([]);
            this.snapTolerance.set(0);
        });

        effect(() => {
            if (this.mapEditModeService.activeEditLayerIds().size) {
                return;
            }

            this.resetEditData();
        });
    }

    public cancelSelectedVertex(): void {
        this.mapEditModeService.isEditingGeometryActive.set(false);
        this.mapEditModeService.isAddVertexActive.set(false);
        this.mapEditModeService.isEditVertexActive.set(false);

        this.resetPhantomLineStyle();
        this.resetSnapStyle();

        this.nextPoints = [];
        this.previousPoints = [];
        this.geomEditContexts.set({});
    }

    public async drawSource(source: MapBaseSource<unknown>): Promise<void> {
        const zoomMaxMin = {
            maxZoom: this.mapEditModeService.activeEditLayerIds().has(source.sourceId) ? 0 : source.maxzoom,
            minZoom: this.mapEditModeService.activeEditLayerIds().has(source.sourceId) ? 0 : source.minzoom,
        };

        const geohashes: string[] = [];

        if (this.mapGeohashService.centralGeohash) {
            geohashes.push(
                ...[
                    ...this.geohashService.findNeighbors(
                        this.mapGeohashService.centralGeohash,
                        MapGeohashService.geohashNeighborsMatrixSize,
                    ),
                    this.mapGeohashService.centralGeohash,
                ],
            );
        }

        await this.mapSourceService.drawSources([{ ...source, ...zoomMaxMin }], { fitBounds: false, geohashes }, true);

        if (
            !this.mapEditModeService.activeEditLayerIds().has(source.sourceId) ||
            this.mapEditModeService.activeGeometryType()
        ) {
            return;
        }

        await this.mapSourceService.applyEditStyle([source.sourceId], this.mapEditModeService.editEntityId());
    }

    public async handleAddLineVertex(addLineVertexFeature: MapGeoJSONFeature): Promise<void> {
        if (!this.drawEditGeomPhantomLineFunctionInstance) {
            this.drawEditGeomPhantomLineFunctionInstance = this.drawEditGeomPhantomLine.bind(this);
        }

        this.mapEditModeService.isAddLineVertexActive.set(true);

        await this.handleAddVertex(addLineVertexFeature);
    }

    public async initEditGeometry(): Promise<void> {
        if (!this.map) {
            return;
        }

        await this.mapSourceService.applySnapStyle(this.mapEditModeService.selectedSnapSources());
        this.map.getCanvas().style.cursor = TARGET_CURSOR;

        if (!this.drawEditGeomPhantomLineFunctionInstance) {
            this.drawEditGeomPhantomLineFunctionInstance = this.drawEditGeomPhantomLine.bind(this);
        }

        this.map.on('mousemove', this.drawEditGeomPhantomLineFunctionInstance);
    }

    public async manageEditEntity(
        mapClickEvent: MapMouseEvent,
        vertexSources?: MapGeoJSONFeature[],
        isAddVertex?: boolean,
    ): Promise<void> {
        if (!this.drawEditGeomPhantomLineFunctionInstance) {
            this.drawEditGeomPhantomLineFunctionInstance = this.drawEditGeomPhantomLine.bind(this);
        }

        if (this.mapEditModeService.isAddLineVertexActive()) {
            this.mapEditModeService.isEditingGeometryActive.set(true);

            return;
        }

        this.mapEditModeService.isEditingGeometryActive.update((value: boolean) => !value);

        if (!this.mapEditModeService.isEditingGeometryActive()) {
            await this.manageSaveEditedEntity(mapClickEvent);

            await this.mapSourceService.applyEditStyle(
                [...this.mapEditModeService.activeEditLayerIds()],
                this.mapEditModeService.editEntityId(),
            );

            this.nextPoints = [];
            this.previousPoints = [];
            this.geomEditContexts.set({});

            return;
        }

        if (isAddVertex && vertexSources?.[0]) {
            await this.handleAddVertex(vertexSources[0]);

            return;
        }

        if (!vertexSources?.length) {
            this.mapEditModeService.isEditingGeometryActive.set(false);

            await this.mapSourceService.applyEditStyle([...this.mapEditModeService.activeEditLayerIds()]);

            return;
        }

        for (const editVertexFeature of vertexSources ?? []) {
            const layerId: string | undefined = editVertexFeature?.properties['layerId'];
            const entityId: string | undefined = editVertexFeature?.properties['entityId'];

            if (!layerId || !entityId) {
                continue;
            }

            if (!this.geomEditContexts()[entityId]) {
                this.geomEditContexts.update((geomEditContexts) => {
                    return { ...geomEditContexts, [entityId]: [] };
                });
            }

            this.editedLayerId().add(layerId);

            await this.calculatePreviousAndNextPoint(editVertexFeature, layerId, entityId);
        }

        this.resetEditModeLayer();

        await this.initEditGeometry();
    }

    public async manageSaveEditedEntity(mapClickEvent: MapMouseEvent): Promise<void> {
        this.resetPhantomLineStyle();
        this.resetSnapStyle();

        if (!Object.values(this.geomEditContexts()).length) {
            return;
        }

        const after: Record<string, unknown> = {};
        const before: Record<string, unknown> = {};

        const newPosition = this.selectedSnapPoint()?.position ?? [mapClickEvent.lngLat.lng, mapClickEvent.lngLat.lat];

        for (const [entityId, geomEditContexts] of Object.entries(this.geomEditContexts()!)) {
            if (!geomEditContexts.length || !geomEditContexts[0].geometry) {
                continue;
            }

            let afterGeom: ComplexGeometryWithError | SimpleGeometryWithError | null = null;
            switch (geomEditContexts[0].geometry.type) {
                case SdkPoint.TypeEnum.POINT:
                    after['geom'] = {
                        bbox: geomEditContexts[0].geometry?.bbox,
                        coordinates: newPosition,
                        type: SdkPoint.TypeEnum.POINT,
                    };
                    before['geom'] = geomEditContexts[0].geometry;
                    break;
                case Polygon.TypeEnum.POLYGON:
                case LineString.TypeEnum.LINE_STRING:
                case MultiPoint.TypeEnum.MULTI_POINT:
                    before['geom'] = structuredClone(geomEditContexts[0].geometry);
                    afterGeom = await this.getUpdatedGeometries(
                        newPosition,
                        geomEditContexts[0].geometry,
                        geomEditContexts.map((geomEditContext) => geomEditContext.selectedPointIndex),
                        geomEditContexts[0].addVertex,
                    );
                    if (afterGeom.error) {
                        this.snackBarService.openErrorSnackBar(`validateGeometry.${afterGeom.error}`);

                        continue;
                    }
                    after['geom'] = afterGeom.geometry;
                    break;
                case MultiPolygon.TypeEnum.MULTI_POLYGON:
                case MultiLineString.TypeEnum.MULTI_LINE_STRING:
                    before['geom'] = structuredClone(geomEditContexts[0].geometry!);
                    afterGeom = await this.getUpdatedMultiGeometries(
                        newPosition,
                        geomEditContexts[0].geometry,
                        geomEditContexts.map((geomEditContext) => geomEditContext.multiGeomIndex),
                        geomEditContexts.map((geomEditContext) => geomEditContext.selectedPointIndex),
                        geomEditContexts[0].addVertex,
                    );
                    if (afterGeom.error) {
                        this.snackBarService.openErrorSnackBar(`validateGeometry.${afterGeom.error}`);

                        continue;
                    }
                    after['geom'] = afterGeom.geometry;
                    break;
            }

            const layer = await this.getLayer(geomEditContexts[0].layerId);
            AssertService.isString(layer.tableName);

            const editEvent: EditEventAction = {
                action: EditAction.UPDATE,
                after,
                before,
                businessIdentifier: this.businessIdentifier(),
                entityId: entityId,
                layerId: geomEditContexts[0].layerId,
                tableName: layer.tableName,
            };

            if (this.shouldStoreGeometryPreviewOnly(entityId)) {
                this.geometryEditPreviewService.set({
                    after: structuredClone(after['geom']) as Geometry,
                    before: structuredClone(before['geom']) as Geometry,
                    businessIdentifier: this.businessIdentifier(),
                    entityId,
                    layerId: geomEditContexts[0].layerId,
                    tableName: layer.tableName,
                });
                await this.redrawSource(geomEditContexts[0].layerId);

                continue;
            }

            this.editModeStateService.addEdit(this.projectBloc.value()!.id, { actions: [editEvent] });
        }
    }

    public mapOffSnapVertexHighlightEvent(): void {
        if (!this.snapVertexHighlightFunctionInstance) {
            this.snapVertexHighlightFunctionInstance = this.handleSnapVertex.bind(this);
        }

        this.map?.off('mousemove', this.snapVertexHighlightFunctionInstance);
    }

    public mapOnSnapVertexHighlightEvent(): void {
        if (!this.snapVertexHighlightFunctionInstance) {
            this.snapVertexHighlightFunctionInstance = this.handleSnapVertex.bind(this);
        }

        this.map?.on('mousemove', this.snapVertexHighlightFunctionInstance);
    }

    public resetAddLineVertex(): void {
        this.mapEditModeService.isEditingGeometryActive.set(false);
        this.mapEditModeService.isAddLineVertexActive.set(false);
        this.mapEditModeService.isAddVertexActive.set(false);
        this.mapEditModeService.isEditVertexActive.set(false);

        this.resetPhantomLineStyle();

        this.nextPoints = [];
        this.previousPoints = [];
        this.geomEditContexts.set({});
    }

    public resetPhantomLineStyle(): void {
        if (!this.map) {
            return;
        }

        this.map.off('mousemove', this.drawEditGeomPhantomLineFunctionInstance!);
        this.map.getCanvas().style.cursor = 'grab';

        const layer = this.map.getLayer(MapEditGeomService.MAP_EDIT_PHANTOM_LAYER_ID);
        const source = this.map.getSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID);

        if (layer) {
            this.map.removeLayer(MapEditGeomService.MAP_EDIT_PHANTOM_LAYER_ID);
        }

        if (!source) {
            return;
        }

        this.map.removeSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID);
    }

    public resetSnapStyle(): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (this.map.getLayer(MapEditModeService.EDIT_SNAP_LAYER)) {
            this.map.removeLayer(MapEditModeService.EDIT_SNAP_LAYER);
        }

        if (!this.map.getSource(MapEditModeService.EDIT_SNAP_SOURCE)) {
            return;
        }

        this.map.removeSource(MapEditModeService.EDIT_SNAP_SOURCE);
    }

    public async setGeomEditContextForAddLineVertexMode(): Promise<void> {
        const geomEditContext = Object.values(this.geomEditContexts()).flat()[0];

        if (!geomEditContext) {
            return;
        }

        geomEditContext.addVertex = true;

        const geometry = await this.getInitialGeom(geomEditContext.layerId, geomEditContext.entityId);

        if (!geometry) {
            return;
        }

        let coordinates: Position[] = [];
        geomEditContext.geometry = geometry;

        if (geometry.type === LineString.TypeEnum.LINE_STRING) {
            AssertService.assertLineStringType(geometry);
            coordinates = geometry.coordinates;
        }

        if (geometry.type === MultiLineString.TypeEnum.MULTI_LINE_STRING) {
            AssertService.assertMultiLineStringType(geometry);
            coordinates = geometry.coordinates[geomEditContext.multiGeomIndex];
        }

        if (geomEditContext.selectedPointIndex === 0) {
            this.nextPoints = [coordinates[0]];
            geomEditContext.selectedPointIndex = 0;
        }

        if (geomEditContext.selectedPointIndex === coordinates.length - 1) {
            this.previousPoints = [coordinates[coordinates.length - 1]];
            geomEditContext.selectedPointIndex = coordinates.length;
        }

        this.geomEditContexts.set({ [geomEditContext.entityId]: [geomEditContext] });

        await this.initEditGeometry();
    }

    private buildPhantomLineFeature(mouseCoordinates: LngLat, pointCoordinates: Position): Feature {
        const coordinates = [
            [pointCoordinates[0], pointCoordinates[1]],
            [mouseCoordinates.lng, mouseCoordinates.lat],
        ];

        return {
            geometry: {
                coordinates: coordinates,
                type: 'LineString',
            },
            properties: {
                id: 'phantom-line',
            },
            type: Feature.TypeEnum.FEATURE,
        };
    }

    private async calculatePreviousAndNextPoint(
        editVertexSource: MapGeoJSONFeature,
        layerId: string,
        entityId: string,
    ): Promise<void> {
        const geometry: Geometry | undefined = await this.getInitialGeom(layerId, entityId);

        if (!geometry || !editModeComplexType.includes(geometry.type)) {
            return;
        }

        if (pointType.includes(geometry.type)) {
            this.handleEditPoint(geometry, editVertexSource, layerId, entityId);

            return;
        }

        const pointIndex: number = editVertexSource.properties[POINT_INDEX];
        const multiIndex: number = editVertexSource.properties['multiIndex'];
        const selectedPointIndex: [number, number] = [multiIndex, pointIndex];

        const { coordinates, geomEditContext } = this.getGeomEditContextAndCoordinates(
            geometry,
            selectedPointIndex,
            layerId,
            entityId,
        );

        this.geomEditContexts.update((geomEditContexts) => {
            return { ...geomEditContexts, [entityId]: [...geomEditContexts[entityId], geomEditContext] };
        });

        this.setPreviousAndNextPoint(polygonType.includes(geometry.type), coordinates, selectedPointIndex[1]);
    }

    private distanceMeters(
        coordinate: { lat: number; lng: number },
        selectedPoint: { lat: number; lng: number },
    ): number {
        const degreesLatitude = ((selectedPoint.lat - coordinate.lat) * Math.PI) / 180;
        const degreesLongitude = ((selectedPoint.lng - coordinate.lng) * Math.PI) / 180;
        const coordinateLatitude = (coordinate.lat * Math.PI) / 180;
        const selectedPointLatitude = (selectedPoint.lat * Math.PI) / 180;

        const haversine =
            Math.sin(degreesLatitude / 2) ** 2 +
            Math.cos(coordinateLatitude) * Math.cos(selectedPointLatitude) * Math.sin(degreesLongitude / 2) ** 2;

        return 2 * MapEditGeomService.EARTH_RADIUS * Math.asin(Math.sqrt(haversine));
    }

    private async drawEditGeomPhantomLine(mapMouseEvent: MapMouseEvent): Promise<void> {
        if (!this.map) {
            return;
        }

        const position = this.handleSnapVertex(mapMouseEvent);
        const phantomLineFeatures: Feature[] = this.getPhantomLineFeatures(position);

        let phantomSource = this.map?.getSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID) as GeoJSONSource;
        if (!phantomSource) {
            this.mapSourceService.addCustomSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID);
            phantomSource = this.map?.getSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID) as GeoJSONSource;
        }

        const phantomDataSource = (await phantomSource.getData()) as FeatureCollection;

        phantomDataSource.features = phantomLineFeatures;
        phantomSource.setData(phantomDataSource as GeoJSON);

        if (!this.map.getSource(MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID)) {
            return;
        }

        this.mapSourceService.addPhantomLineLayer(
            phantomSource,
            MapEditGeomService.MAP_EDIT_PHANTOM_SOURCE_ID,
            MapEditGeomService.MAP_EDIT_PHANTOM_LAYER_ID,
            getComputedStyle(document.documentElement).getPropertyValue('--custom-blue').trim(),
        );
    }

    private getClosestFeature(mapMouseEvent: MapMouseEvent): MapGeoJSONFeature | null {
        const bbox: [PointLike, PointLike] = [
            [mapMouseEvent.point.x - this.snapTolerance(), mapMouseEvent.point.y - this.snapTolerance()],
            [mapMouseEvent.point.x + this.snapTolerance(), mapMouseEvent.point.y + this.snapTolerance()],
        ];

        const snapVertexFeatures = this.map?.queryRenderedFeatures(bbox, {
            layers: [MapEditModeService.EDIT_SNAP_LAYER],
        });

        if (!snapVertexFeatures?.length) {
            return null;
        }

        return snapVertexFeatures
            .map((snapVertexFeature: MapGeoJSONFeature) => {
                AssertService.assertPointType(snapVertexFeature.geometry);
                const coordinates: [number, number] = [
                    snapVertexFeature.geometry.coordinates[0],
                    snapVertexFeature.geometry.coordinates[1],
                ];
                const screenPt = this.map!.project(coordinates);

                const distance = Math.hypot(screenPt.x - mapMouseEvent.point.x, screenPt.y - mapMouseEvent.point.y);

                return { distance, feature: snapVertexFeature };
            })
            .sort((a, b) => a.distance - b.distance)[0].feature;
    }

    private getGeomEditContextAndCoordinates(
        geom: Geometry,
        selectedPointIndex: [number, number],
        layerId: string,
        entityId: string,
    ): { coordinates: Position[]; geomEditContext: GeomEditContext } {
        const geomEditContext: GeomEditContext = {
            addVertex: false,
            entityId,
            geometry: geom,
            layerId,
            multiGeomIndex: selectedPointIndex[0],
            selectedPointIndex: selectedPointIndex[1],
        };

        let coordinates: Position[] = [];

        switch (geom.type) {
            case Polygon.TypeEnum.POLYGON: {
                AssertService.assertPolygonType(geom);
                coordinates = geom.coordinates[0];
                break;
            }
            case LineString.TypeEnum.LINE_STRING: {
                AssertService.assertLineStringType(geom);
                coordinates = geom.coordinates;
                break;
            }
            case MultiPolygon.TypeEnum.MULTI_POLYGON: {
                AssertService.assertMultiPolygonType(geom);
                coordinates = geom.coordinates[selectedPointIndex[0]][0];
                break;
            }
            case MultiLineString.TypeEnum.MULTI_LINE_STRING: {
                AssertService.assertMultiLineStringType(geom);
                coordinates = geom.coordinates[selectedPointIndex[0]];
                break;
            }
            default: {
                throw new Error(`Unsupported geometry type`);
            }
        }

        return { coordinates, geomEditContext };
    }

    private getIndexOfSelectedPoint(
        coordinates: Position[],
        selectedPoint: { lat: number; lng: number },
        maxDistanceMeters = 30,
    ): number {
        let bestIdx = -1;
        let bestDist = Infinity;

        for (let i = 0; i < coordinates.length; i++) {
            const [lng, lat] = coordinates[i];
            const d = this.distanceMeters({ lat, lng }, selectedPoint);
            if (d < bestDist) {
                bestDist = d;
                bestIdx = i;
            }
        }

        return bestDist <= maxDistanceMeters ? bestIdx : -1;
    }

    private async getInitialGeom(layerId: string, entityId: string): Promise<Geometry | undefined> {
        const previewGeometry = this.geometryEditPreviewService.getGeometry(layerId, entityId);

        if (previewGeometry) {
            const preview = this.geometryEditPreviewService.get(layerId, entityId);

            if (preview) {
                this.businessIdentifier.set(preview.businessIdentifier);
            }

            return structuredClone(previewGeometry) as Geometry;
        }

        const entityState = this.editModeStateService.getEntityState(this.projectBloc.value()!.id, layerId, entityId);

        let initialGeom: Geometry | undefined = entityState?.properties?.['geom'];

        if (entityState?.businessIdentifier) {
            AssertService.hasKey(entityState.businessIdentifier, 'key');
            AssertService.isString(entityState.businessIdentifier['key']);
            AssertService.hasKey(entityState.businessIdentifier, 'value');

            this.businessIdentifier.set({
                key: entityState.businessIdentifier['key'],
                value: entityState.businessIdentifier['value'],
            });
        }

        if (!initialGeom) {
            const [entity, layer] = await Promise.all([
                this.getSelectedEntity(layerId, entityId),
                this.getLayer(layerId),
            ]);
            const businessIdentifierKey = layer.businessIdentifier ?? 'uuid';
            this.businessIdentifier.set({ key: businessIdentifierKey, value: entity.businessIdentifier });

            initialGeom = entity.geom;
        }

        return initialGeom;
    }

    private getLayer(layerId: string): Promise<Layer> {
        return firstValueFrom(this.sdkLayersService.layerControllerGetOne({ id: layerId }));
    }

    private getPhantomLineFeatures(position: LngLat): Feature[] {
        const features: Feature[] = [];

        if (this.previousPoints.length) {
            this.previousPoints.forEach((previousPoint) => {
                features.push(this.buildPhantomLineFeature(position, previousPoint));
            });
        }

        if (this.nextPoints.length) {
            this.nextPoints.forEach((nextPoint) => {
                features.push(this.buildPhantomLineFeature(position, nextPoint));
            });
        }

        return features;
    }

    private getSelectedEntity(layerId: string, entityId: string): Promise<Entity> {
        return firstValueFrom(
            this.sdkLayersService.layerControllerGetLayersEntity({ entityId: entityId, id: layerId }),
        );
    }

    private getUpdatedGeometries(
        newPoint: Position,
        geometry: Geometry,
        selectedPointIndexes: number[],
        addVertex: boolean,
    ): Promise<SimpleGeometryWithError> {
        try {
            const bbox = geometry.bbox;

            if (geometry.type === Polygon.TypeEnum.POLYGON) {
                AssertService.assertPolygonType(geometry);
                const openRing = geometry.coordinates[0].slice(0, -1);

                selectedPointIndexes.forEach((selectedPointIndex: number) => {
                    const safeIndex = selectedPointIndex % openRing.length;
                    openRing.splice(safeIndex, addVertex ? 0 : 1, newPoint);
                });

                const newCoords = [...openRing, openRing[0]];
                const newGeometry = { ...geometry, coordinates: [newCoords] };

                return this.validateGeometryAndReturn(
                    wktStringify({ ...newGeometry }),
                    bbox,
                    AssertService.assertPolygonType,
                );
            }

            selectedPointIndexes.forEach((selectedPointIndex) =>
                geometry.coordinates.splice(selectedPointIndex, addVertex ? 0 : 1, newPoint),
            );

            if (geometry.type === MultiPoint.TypeEnum.MULTI_POINT) {
                AssertService.assertMultiPointType(geometry);

                return new Promise((resolve) =>
                    resolve({
                        error: null,
                        geometry: {
                            bbox,
                            coordinates: geometry.coordinates,
                            type: MultiPoint.TypeEnum.MULTI_POINT,
                        },
                    }),
                );
            }

            return this.validateGeometryAndReturn(
                wktStringify({ ...geometry }),
                bbox,
                AssertService.assertLineStringType,
            );
        } catch {
            return new Promise((resolve) => resolve({ error: 'unknownError', geometry: null }));
        }
    }

    private getUpdatedMultiGeometries(
        newPoint: Position,
        geometry: Geometry,
        multiGeomIndexes: number[],
        selectedPointIndexes: number[],
        addVertex: boolean,
    ): Promise<ComplexGeometryWithError> {
        try {
            const bbox = geometry.bbox;

            if (geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON) {
                AssertService.assertMultiPolygonType(geometry);
                selectedPointIndexes.forEach((selectedPointIndex, index) =>
                    geometry.coordinates[multiGeomIndexes[index]][0].splice(
                        selectedPointIndex,
                        addVertex ? 0 : 1,
                        newPoint,
                    ),
                );

                return this.validateGeometryAndReturn(
                    wktStringify({ ...geometry }),
                    bbox,
                    AssertService.assertMultiPolygonType,
                );
            }

            AssertService.assertMultiLineStringType(geometry);

            selectedPointIndexes.forEach((selectedPointIndex, index) =>
                geometry.coordinates[multiGeomIndexes[index]].splice(selectedPointIndex, addVertex ? 0 : 1, newPoint),
            );

            return this.validateGeometryAndReturn(
                wktStringify({ ...geometry }),
                bbox,
                AssertService.assertMultiLineStringType,
            );
        } catch {
            return new Promise((resolve) => resolve({ error: 'unknownError', geometry: null }));
        }
    }

    private async handleAddVertex(addVertexSource: MapGeoJSONFeature): Promise<void> {
        const addVertexIndex: number = addVertexSource.properties[POINT_INDEX];
        const layerId: string | undefined = addVertexSource.properties['layerId'];
        const entityId: string | undefined = addVertexSource.properties['entityId'];
        const ringIndex: number = addVertexSource.properties['multiIndex'] ?? 0;

        if (!layerId || !entityId) {
            this.mapEditModeService.isEditingGeometryActive.set(false);

            return;
        }

        if (!this.geomEditContexts()[entityId]) {
            this.geomEditContexts.update((geomEditContexts) => {
                return { ...geomEditContexts, [entityId]: [] };
            });
        }

        this.currentEntityId.set(entityId);

        const initialGeom: Geometry | undefined = await this.getInitialGeom(layerId, entityId);

        this.editedLayerId().add(layerId);

        if (!initialGeom) {
            return;
        }

        this.resetEditModeLayer();

        this.setAddVertexGeomEditContext(layerId, entityId, initialGeom, addVertexIndex, ringIndex);

        await this.initEditGeometry();
    }

    private handleEditPoint(geometry: Geometry, vertexHit: MapGeoJSONFeature, layerId: string, entityId: string): void {
        const geomEditContext: GeomEditContext = {
            addVertex: false,
            entityId,
            geometry: geometry,
            layerId,
            multiGeomIndex: 0,
            selectedPointIndex: 0,
        };

        if (geometry.type === MultiPoint.TypeEnum.MULTI_POINT && vertexHit.geometry.type === SdkPoint.TypeEnum.POINT) {
            AssertService.assertMultiPointType(geometry);
            const [lng, lat] = vertexHit.geometry.coordinates;
            geomEditContext.selectedPointIndex = this.getIndexOfSelectedPoint(geometry.coordinates, { lat, lng });
        }

        this.geomEditContexts.update((geomEditContexts) => {
            return {
                ...geomEditContexts,
                [entityId]: [...geomEditContexts[entityId], geomEditContext],
            };
        });
    }

    private handleSnapVertex(mapMouseEvent: MapMouseEvent): LngLat {
        let position = mapMouseEvent.lngLat;

        if (
            !this.mapEditModeService.selectedSnapSources().length ||
            !this.map?.getLayer(MapEditModeService.EDIT_SNAP_LAYER)
        ) {
            return position;
        }

        const closestSnapFeature = this.getClosestFeature(mapMouseEvent);

        if (!closestSnapFeature) {
            this.resetSnapHighlight();
            this.selectedSnapPoint.set(null);

            return position;
        }

        AssertService.assertPointType(closestSnapFeature.geometry);

        position = new LngLat(closestSnapFeature.geometry.coordinates[0], closestSnapFeature.geometry.coordinates[1]);

        const source = this.map?.getSource(MapEditModeService.EDIT_SNAP_SOURCE);
        const uniqueId: string | undefined = closestSnapFeature.properties['uniqueId'];

        if (!source || !uniqueId) {
            return position;
        }

        this.highlightSnapVertex(uniqueId);

        if (!this.selectedSnapPoint() || this.selectedSnapPoint()?.uniqueId !== uniqueId) {
            this.selectedSnapPoint.set({
                position: closestSnapFeature.geometry.coordinates,
                uniqueId,
            });
        }

        return position;
    }

    private highlightSnapVertex(uniqueId: string): void {
        this.mapHighlightEntityService.updatePaintProperties(
            MapEditModeService.EDIT_SNAP_LAYER,
            [uniqueId],
            'uniqueId',
            layerSymbolStyle.highlight,
            DEFAULT_SNAP_LAYOUT_STYLE,
        );

        this.mapHighlightEntityService.updateLayoutProperty(
            MapEditModeService.EDIT_SNAP_LAYER,
            ['icon-size', 'icon-image'],
            [uniqueId],
            'uniqueId',
            [2, 'outline_diamond'],
            [0.6, 'diamond'],
        );
    }

    private async redrawSource(layerId: string): Promise<void> {
        const source = this.sourcesService.getSource(layerId);

        if (!source) {
            return;
        }

        await this.drawSource(source);
    }

    private resetEditData(): void {
        this.geomEditContexts.set({});
        this.previousPoints = [];
        this.nextPoints = [];
        this.mapEditModeService.isEditVertexActive.set(false);
        this.mapEditModeService.isEditingGeometryActive.set(false);
        this.mapEditModeService.isAddVertexActive.set(false);
        this.mapEditModeService.isAddLineVertexActive.set(false);
    }

    private resetEditModeLayer(): void {
        if (this.map!.getLayer(MapEditModeService.EDIT_LAYER)) {
            this.map!.removeLayer(MapEditModeService.EDIT_LAYER);
        }

        if (!this.map!.getLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER)) {
            return;
        }

        this.map!.removeLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER);
    }

    private resetSnapHighlight(): void {
        if (!this.selectedSnapPoint()) {
            return;
        }

        this.mapHighlightEntityService.resetLayoutAndPaintProperties(
            MapEditModeService.EDIT_SNAP_LAYER,
            ['icon-size', 'icon-image'],
            [0.6, 'diamond'],
            DEFAULT_SNAP_LAYOUT_STYLE,
        );
    }

    private setAddVertexGeomEditContext(
        layerId: string,
        entityId: string,
        geometry: Geometry,
        addVertexIndex: number,
        ringIndex: number,
    ): void {
        const geomEditContext: GeomEditContext = {
            addVertex: true,
            entityId,
            geometry: geometry,
            layerId,
            multiGeomIndex: ringIndex,
            selectedPointIndex: addVertexIndex === 0 ? 0 : addVertexIndex + 1,
        };

        let geomCoordinates: Position[] = [];

        if (geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON) {
            AssertService.assertMultiPolygonType(geometry);
            geomCoordinates = geometry.coordinates[ringIndex].flat();
            this.previousPoints = [geomCoordinates[addVertexIndex]];
            this.nextPoints = [geomCoordinates[addVertexIndex + 1]];
        }

        if (geometry.type === Polygon.TypeEnum.POLYGON) {
            AssertService.assertPolygonType(geometry);
            geomCoordinates = geometry.coordinates.flat();
            this.previousPoints = [geomCoordinates[addVertexIndex]];
            this.nextPoints = [geomCoordinates[addVertexIndex + 1]];
        }

        if (geometry.type === MultiLineString.TypeEnum.MULTI_LINE_STRING) {
            AssertService.assertMultiLineStringType(geometry);
            geomCoordinates = geometry.coordinates[ringIndex];

            if (addVertexIndex === 0) {
                this.nextPoints = [geomCoordinates[0]];
            }

            if (addVertexIndex === geomCoordinates.length - 1) {
                this.previousPoints = [geomCoordinates[addVertexIndex]];
            }
        }

        if (geometry.type === LineString.TypeEnum.LINE_STRING) {
            AssertService.assertLineStringType(geometry);
            geomCoordinates = structuredClone(geometry.coordinates);

            if (addVertexIndex === 0) {
                this.nextPoints = [geomCoordinates[0]];
            }

            if (addVertexIndex === geomCoordinates.length - 1) {
                this.previousPoints = [geomCoordinates[addVertexIndex]];
            }
        }

        this.geomEditContexts.update((geomEditContexts) => {
            return { ...geomEditContexts, [entityId]: [...geomEditContexts[entityId], geomEditContext] };
        });
    }

    private setPreviousAndNextPoint(isPolygonType: boolean, coordinates: Position[], selectedPointIndex: number): void {
        const lastIndex = coordinates.length - 1;

        if (isPolygonType) {
            this.previousPoints.push(
                //Here we use lastIndex - 1 because the first index and last are the same values in polygon type
                selectedPointIndex === 0 ? coordinates[lastIndex - 1] : coordinates[selectedPointIndex - 1],
            );
            this.nextPoints.push(
                //Here we use coordinates[1] for the same reason as above
                selectedPointIndex === lastIndex ? coordinates[1] : coordinates[selectedPointIndex + 1],
            );

            return;
        }

        if (selectedPointIndex) {
            this.previousPoints.push(coordinates[selectedPointIndex - 1]);
        }

        if (selectedPointIndex === lastIndex) {
            return;
        }

        this.nextPoints.push(coordinates[selectedPointIndex + 1]);
    }

    private shouldStoreGeometryPreviewOnly(entityId: string): boolean {
        return this.mapEditModeService.editEntityId() === entityId;
    }

    private async validateGeometryAndReturn<T extends Geometry>(
        wktGeometry: string,
        bbox: Geometry['bbox'],
        assert: (geometry: Geometry | null | undefined) => asserts geometry is T,
    ): Promise<{ error: string | null; geometry: T | null }> {
        const validateGeometry = await firstValueFrom(
            this.geometryService.geometryControllerValidateGeometry({ validateGeometryDto: { wktGeometry } }),
        );

        if (!validateGeometry.valid) {
            return { error: validateGeometry.reason, geometry: null };
        }

        assert(validateGeometry.geometry);

        return { error: null, geometry: { ...validateGeometry.geometry, bbox } };
    }
}
